import React, { useState, useEffect } from 'react';

const ProjectForm = ({ project, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    id: '',
    title: '',
    shortDescription: '',
    fullDescription: '',
    thumbnail: '',
    images: [],
    tags: [],
    links: { demo: '', github: '' },
    features: [],
    techStack: [],
    challenges: ''
  });

  const [newImage, setNewImage] = useState('');
  const [newTag, setNewTag] = useState('');
  const [newFeature, setNewFeature] = useState('');
  const [newTech, setNewTech] = useState('');
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (project) {
      setFormData({
        ...project,
        images: Array.isArray(project.images) ? project.images : (typeof project.images === 'string' ? JSON.parse(project.images || '[]') : []),
        tags: Array.isArray(project.tags) ? project.tags : (typeof project.tags === 'string' ? JSON.parse(project.tags || '[]') : []),
        features: Array.isArray(project.features) ? project.features : (typeof project.features === 'string' ? JSON.parse(project.features || '[]') : []),
        techStack: Array.isArray(project.techStack) ? project.techStack : (typeof project.techStack === 'string' ? JSON.parse(project.techStack || '[]') : []),
        links: typeof project.links === 'string' ? JSON.parse(project.links || '{}') : (project.links || {})
      });
    }
  }, [project]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (!formData.shortDescription.trim()) newErrors.shortDescription = 'Short description is required';
    if (!formData.fullDescription.trim()) newErrors.fullDescription = 'Full description is required';
    if (formData.images.length === 0) newErrors.images = 'At least one image is required';
    if (formData.techStack.length === 0) newErrors.techStack = 'At least one technology is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const addImage = () => {
    if (newImage.trim()) {
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, newImage.trim()]
      }));
      setNewImage('');
    }
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const addTag = () => {
    if (newTag.trim()) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const removeTag = (index) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter((_, i) => i !== index)
    }));
  };

  const addFeature = () => {
    if (newFeature.trim()) {
      setFormData(prev => ({
        ...prev,
        features: [...prev.features, newFeature.trim()]
      }));
      setNewFeature('');
    }
  };

  const removeFeature = (index) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  const addTech = () => {
    if (newTech.trim()) {
      setFormData(prev => ({
        ...prev,
        techStack: [...prev.techStack, newTech.trim()]
      }));
      setNewTech('');
    }
  };

  const removeTech = (index) => {
    setFormData(prev => ({
      ...prev,
      techStack: prev.techStack.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSave({
        ...formData,
        thumbnail: formData.images[0] || '',
        images: formData.images,
        tags: formData.tags,
        features: formData.features,
        techStack: formData.techStack,
        links: formData.links
      });
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ maxHeight: '70vh', overflowY: 'auto', paddingRight: '10px' }}>
      <h3>{project ? 'Edit Project' : 'Add New Project'}</h3>

      <div className="form-group">
        <label htmlFor="title">Project Title *</label>
        <input
          id="title"
          type="text"
          name="title"
          value={formData.title}
          onChange={handleChange}
          placeholder="e.g., E-commerce Platform"
        />
        {errors.title && <div style={{ color: '#d32f2f', fontSize: '12px', marginTop: '5px' }}>{errors.title}</div>}
      </div>

      <div className="form-group">
        <label htmlFor="shortDescription">Short Description *</label>
        <input
          id="shortDescription"
          type="text"
          name="shortDescription"
          value={formData.shortDescription}
          onChange={handleChange}
          placeholder="Brief description (one line)"
        />
        {errors.shortDescription && <div style={{ color: '#d32f2f', fontSize: '12px', marginTop: '5px' }}>{errors.shortDescription}</div>}
      </div>

      <div className="form-group">
        <label htmlFor="fullDescription">Full Description *</label>
        <textarea
          id="fullDescription"
          name="fullDescription"
          value={formData.fullDescription}
          onChange={handleChange}
          placeholder="Detailed project description..."
          style={{ width: '100%', padding: '10px', minHeight: '80px', fontFamily: 'inherit', border: '1px solid #ddd', borderRadius: '5px', fontSize: '14px' }}
        />
        {errors.fullDescription && <div style={{ color: '#d32f2f', fontSize: '12px', marginTop: '5px' }}>{errors.fullDescription}</div>}
      </div>

      <div className="form-group">
        <label>📸 Project Images * ({formData.images.length})</label>
        <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
          <input
            type="url"
            value={newImage}
            onChange={(e) => setNewImage(e.target.value)}
            placeholder="Image URL"
            onKeyPress={(e) => e.key === 'Enter' && addImage()}
            style={{ flex: 1, padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
          />
          <button type="button" onClick={addImage} style={{ padding: '8px 12px', backgroundColor: '#4CAF50', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Add Image</button>
        </div>
        <div style={{ marginBottom: '10px' }}>
          {formData.images.map((img, idx) => (
            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
              <img src={img} alt={`project-${idx}`} style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '4px' }} />
              <span style={{ flex: 1, fontSize: '12px', wordBreak: 'break-all' }}>{img}</span>
              <button type="button" onClick={() => removeImage(idx)} style={{ padding: '4px 8px', backgroundColor: '#f44336', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>Remove</button>
            </div>
          ))}
        </div>
        {errors.images && <div style={{ color: '#d32f2f', fontSize: '12px', marginTop: '5px' }}>{errors.images}</div>}
      </div>

      <div className="form-group">
        <label>🏷️ Tags</label>
        <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
          <input
            type="text"
            value={newTag}
            onChange={(e) => setNewTag(e.target.value)}
            placeholder="e.g., React"
            onKeyPress={(e) => e.key === 'Enter' && addTag()}
            style={{ flex: 1, padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
          />
          <button type="button" onClick={addTag} style={{ padding: '8px 12px', backgroundColor: '#2196F3', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Add Tag</button>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {formData.tags.map((tag, idx) => (
            <span key={idx} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 12px', backgroundColor: '#2196F3', color: 'white', borderRadius: '20px', fontSize: '12px' }}>
              {tag}
              <button type="button" onClick={() => removeTag(idx)} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', fontSize: '16px', padding: 0 }}>✕</button>
            </span>
          ))}
        </div>
      </div>

      <div className="form-group">
        <label>✨ Features</label>
        <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
          <input
            type="text"
            value={newFeature}
            onChange={(e) => setNewFeature(e.target.value)}
            placeholder="e.g., Real-time updates"
            onKeyPress={(e) => e.key === 'Enter' && addFeature()}
            style={{ flex: 1, padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
          />
          <button type="button" onClick={addFeature} style={{ padding: '8px 12px', backgroundColor: '#FF9800', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Add Feature</button>
        </div>
        <div style={{ marginBottom: '10px' }}>
          {formData.features.map((feat, idx) => (
            <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px', marginBottom: '6px' }}>
              <span>{feat}</span>
              <button type="button" onClick={() => removeFeature(idx)} style={{ padding: '4px 8px', backgroundColor: '#f44336', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>Remove</button>
            </div>
          ))}
        </div>
      </div>

      <div className="form-group">
        <label>⚙️ Tech Stack * ({formData.techStack.length})</label>
        <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
          <input
            type="text"
            value={newTech}
            onChange={(e) => setNewTech(e.target.value)}
            placeholder="e.g., Node.js"
            onKeyPress={(e) => e.key === 'Enter' && addTech()}
            style={{ flex: 1, padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
          />
          <button type="button" onClick={addTech} style={{ padding: '8px 12px', backgroundColor: '#9C27B0', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Add Tech</button>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {formData.techStack.map((tech, idx) => (
            <span key={idx} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 12px', backgroundColor: '#9C27B0', color: 'white', borderRadius: '20px', fontSize: '12px' }}>
              {tech}
              <button type="button" onClick={() => removeTech(idx)} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', fontSize: '16px', padding: 0 }}>✕</button>
            </span>
          ))}
        </div>
        {errors.techStack && <div style={{ color: '#d32f2f', fontSize: '12px', marginTop: '5px' }}>{errors.techStack}</div>}
      </div>

      <div className="form-group">
        <label htmlFor="demoLink">Demo Link</label>
        <input
          id="demoLink"
          type="url"
          value={formData.links?.demo || ''}
          onChange={(e) => setFormData(prev => ({ ...prev, links: { ...prev.links, demo: e.target.value } }))}
          placeholder="https://demo.example.com"
        />
      </div>

      <div className="form-group">
        <label htmlFor="githubLink">GitHub Link</label>
        <input
          id="githubLink"
          type="url"
          value={formData.links?.github || ''}
          onChange={(e) => setFormData(prev => ({ ...prev, links: { ...prev.links, github: e.target.value } }))}
          placeholder="https://github.com/username/repo"
        />
      </div>

      <div className="form-group">
        <label htmlFor="challenges">Challenges & Solutions</label>
        <textarea
          id="challenges"
          name="challenges"
          value={formData.challenges}
          onChange={handleChange}
          placeholder="Describe technical challenges and how you solved them..."
          style={{ width: '100%', padding: '10px', minHeight: '80px', fontFamily: 'inherit', border: '1px solid #ddd', borderRadius: '5px', fontSize: '14px' }}
        />
      </div>

      <div className="modal-actions">
        <button type="button" className="cancel-btn" onClick={onCancel}>Cancel</button>
        <button type="submit" className="save-btn">{project ? 'Update Project' : 'Create Project'}</button>
      </div>
    </form>
  );
};

export default ProjectForm;
